<template>
  <div class="p-6">
    <!-- Header Section (Remains part of the flex flow on mobile) -->
    <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4 flex-shrink-0">
      <!-- Left Group: Title, Refresh, and Mobile Count Tag -->
      <div class="flex items-center justify-between">
          <!-- Sub-group for Title & Refresh -->
          <div class="flex items-center space-x-3 flex-shrink-0">
            <h1 class="text-3xl font-bold text-black">Driver Tracking</h1>
            <button
              @click="fetchDrivers"
              :disabled="loading"
              title="Refresh Data"
              class="p-1.5 rounded-full text-gray-600 hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <!-- Refresh Icon SVG -->
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
           <!-- Cruiser Count Tag (Mobile Only - hidden on md+) -->
          <div class="flex items-center space-x-3 md:hidden">
            <span class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-teal-200 text-teal-900">
               <svg xmlns="http://www.w3.org/2000/svg" class="-ml-0.5 mr-1.5 h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
               </svg>
               {{ cruiserCount }}
               <span class="hidden sm:inline ml-1">Cruisers</span>
             </span>
          </div>
      </div>

      <!-- Right Group: Search & Desktop Count Tag -->
      <div class="flex flex-col md:flex-row items-stretch md:items-center md:space-x-4">
         <!-- Container for Search & Mobile Sort -->
         <div class="flex flex-row items-center gap-2 order-2 md:order-1 flex-grow md:flex-grow-0">
           <!-- Search Input -->
           <div class="relative flex-grow">
             <input
               type="text"
               v-model="searchQuery"
               placeholder="Search cruisers..."
               class="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full"
             />
             <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
               </svg>
             </div>
           </div>
           <!-- Sort Controls (Mobile Only) -->
           <div class="relative md:hidden flex-shrink-0">
              <!-- Custom Sort Button -->
              <button
                @click="isSortMenuOpen = !isSortMenuOpen"
                class="flex items-center justify-between w-full text-sm border border-primary bg-primary text-white hover:bg-primary-600 rounded-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary py-1 px-3"
              >
                 <span>{{ currentSortOptionText }}</span>
                  <!-- Direction Indicator Icon -->
                  <svg v-if="sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                     <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                   </svg>
                   <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                     <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                   </svg>
              </button>
              <!-- Dropdown Menu with Transition -->
              <transition name="sort-menu-fade">
                <div v-if="isSortMenuOpen" class="absolute right-0 mt-2 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 py-1">
                    <a v-for="option in sortOptions"
                       :key="option.value"
                       href="#"
                       @click.prevent="selectSortOption(option.value)"
                       class="flex justify-between items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                       :class="{ 'font-semibold bg-gray-50': sortColumn === option.value }"
                    >
                        <span>{{ option.text }}</span>
                        <!-- Conditional Sort Direction Icon -->
                        <span v-if="sortColumn === option.value">
                           <svg v-if="sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </span>
                    </a>
                </div>
              </transition>
           </div>
    </div>

         <!-- Cruiser Count Tag (Desktop Only) -->
         <div class="hidden md:flex items-center space-x-3 self-end md:self-center order-1 md:order-2">
            <span class="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-teal-200 text-teal-900">
              <svg xmlns="http://www.w3.org/2000/svg" class="-ml-0.5 mr-1.5 h-4 w-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
             {{ cruiserCount }}
             <span class="hidden sm:inline ml-1">Cruisers</span>
           </span>
         </div>
      </div>
    </div>

    <!-- NEW Wrapper Below Header - Calculated Height -->
    <div class="flex flex-col overflow-hidden h-[calc(100vh_-_15rem)]">

      <!-- Container for the Google Map - Inside new wrapper -->
      <div class="map-container rounded-lg overflow-hidden relative flex-shrink-0 min-h-[300px] md:h-2/5">
        <div id="googleMap" style="width: 100%; height: 100%;"></div>
        <!-- Custom Zoom to Fit Button -->
        <button
          @click="zoomToFitAllDrivers"
          title="Zoom to fit all drivers"
          class="absolute top-4 right-4 z-10 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
        >
          <!-- Expand Arrows Icon -->
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v4m0 0h-4m4 0l-5-5" />
          </svg>
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-20 flex justify-center items-center flex-1">
        <svg class="animate-spin h-12 w-12 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Error State -->
      <div v-else-if="apiError" class="text-center py-20 text-red-600 flex-1">
        <p class="text-lg">Error loading tracking data. Please try again later.</p>
      </div>

      <!-- Content Area Wrapper (Table/Cards) - Use flex-1 to fill remaining -->
      <div v-else class="flex-1 overflow-hidden min-h-0 mt-4">

        <!-- Desktop Table View Container - h-full and scroll -->
        <div class="table-container rounded-lg shadow-md overflow-hidden hidden md:block h-full overflow-y-auto">
      <table class="min-w-full table-auto">
            <!-- Make thead sticky and add shadow -->
            <thead class="bg-gray-50 sticky top-0 z-10 shadow-md shadow-gray-200">
          <tr>
                 <!-- NEW Static Number Header -->
                 <th class="py-3 px-3 text-center text-sm font-semibold text-gray-500 border-b border-r border-gray-200 w-12">#</th>
            <th class="py-3 px-3 text-left text-sm font-semibold text-gray-600 border-b border-r border-gray-200">
                    <!-- Cruiser Sort Button -->
                    <button type="button" @click="sortBy('user_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                       <!-- RESTORED Cruiser content -->
                       <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                       </svg>
                       <span class="text-sm">Cruiser</span>
                       <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'user_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                           <svg v-else-if="sortColumn === 'user_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                       </span>
                    </button>
            </th>
                 <!-- NEW Group Header -->
            <th class="py-3 px-3 text-left text-sm font-semibold text-gray-600 border-b border-r border-gray-200">
                    <button type="button" @click="sortBy('group_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Users Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span class="text-sm">Group</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'group_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'group_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
            </th>
            <th class="py-3 px-3 text-left text-sm font-semibold text-gray-600 border-b border-r border-gray-200">
                   <!-- Mobile Number Sort Button -->
                   <button type="button" @click="sortBy('mobile_number')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                      <!-- RESTORED Mobile content -->
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                         <path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span class="text-sm">Mobile</span>
                      <span class="opacity-50 group-hover:opacity-100">
                          <svg v-if="sortColumn === 'mobile_number' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                          <svg v-else-if="sortColumn === 'mobile_number' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                      </span>
                   </button>
            </th>
            <th class="py-3 px-3 text-left text-sm font-semibold text-gray-600 border-b border-r border-gray-200">
                  <button type="button" @click="sortBy('vechile_model')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span class="text-sm">Car Model</span>
                    <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'vechile_model' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'vechile_model' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                  </button>
            </th>
            <th class="py-3 px-3 text-left text-sm font-semibold text-gray-600 border-b border-gray-200 last:border-r-0">
                  <button type="button" @click="sortBy('vechile_plate')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                <span class="text-sm">Plate Number</span>
                    <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'vechile_plate' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'vechile_plate' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                  </button>
            </th>
                <!-- New TH for Action Button -->
                <th class="py-3 px-2 text-center text-sm font-semibold text-gray-600 border-b border-gray-200 w-12"></th>
          </tr>
        </thead>
        <tbody>
              <!-- Add empty state row -->
              <tr v-if="!loading && !apiError && !processedDrivers.length">
                <!-- Update colspan to 7 -->
                <td colspan="7" class="px-6 py-10 text-center text-sm text-gray-500">
                  No tracking data available.
                </td>
              </tr>
              <!-- Data Rows - Add group class and conditional gradient -->
              <tr v-for="(driver, index) in processedDrivers"
                  :key="driver.user_id || driver.mobile_number || index"
                  class="bg-white hover:bg-gray-50 cursor-pointer group transition-colors duration-200 ease-in-out"
                  :class="{ 'bg-gradient-to-l from-teal-100 to-white': driver.mobile_number === hoveredDriverMobile }"
                  @click="zoomToDriver(driver)"
                  @mouseenter="handleDesktopHover(driver)"
                  @mouseleave="handleDesktopLeave"
                 >
                <!-- NEW Static Number Cell -->
                <td class="py-3 px-3 text-sm text-gray-500 text-center border-b border-gray-200 border-r w-12">{{ index + 1 }}</td>
                <!-- Remove Number Column Data Cell -->
                <!--
            <td class="py-3 px-4 text-sm text-gray-700 border-b border-gray-200 border-r">{{ index+1 }}</td>
                -->
                <td class="py-3 px-4 text-sm text-gray-700 border-b border-gray-200 border-r">
                    <!-- Cruiser Name Tag -->
                    <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                       {{ formatUserName(driver['user_name']) }}
                    </span>
                </td>
                <!-- NEW Group Cell -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">
                    <span
                       v-if="driver.group_name"
                       :class="['inline-block', 'px-2', 'py-0.5', 'rounded', 'text-xs', getGroupColor(driver.group_name)]"
                    >
                        {{ driver.group_name?.replace(/dkilo /i, '') || 'N/A' }}
                    </span>
                     <span v-else class="text-gray-500 text-xs">N/A</span>
                </td>
                <td class="py-3 px-4 text-sm text-gray-700 relative group/cell cursor-pointer border-b border-gray-200 border-r" @click.stop="copyToClipboard(driver['mobile_number'], driver['mobile_number'])" title="Click to copy">
                   <span class="inline-block group-hover/cell:hidden">
                  {{ driver['mobile_number'] }}
               </span>
                   <span class="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-teal-100 px-2 py-0.5 rounded shadow whitespace-nowrap opacity-0 scale-95 group-hover/cell:opacity-100 group-hover/cell:scale-100 pointer-events-none group-hover/cell:pointer-events-auto transition-all duration-150 ease-in-out">
                 {{ driver['mobile_number'] }}
               </span>
               <transition name="fade">
                  <span v-if="copiedItemId === driver['mobile_number']" class="absolute -top-1 -right-1 bg-teal-200 text-teal-900 text-[10px] font-bold px-1 py-0 rounded-full shadow-md z-20">
                      Copied!
                  </span>
               </transition>
            </td>
            <td class="py-3 px-4 text-sm text-gray-700 border-b border-gray-200 border-r">{{ driver['vechile_model'] }}</td>
            <td class="py-3 px-4 text-sm text-gray-700 border-b border-gray-200 last:border-r-0">
              <!-- Apply tag styling -->
              <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                {{ formatPlateNumber(driver['vechile_plate']) }}
              </span>
            </td>
                <!-- Last TD - Add relative positioning and overlay -->
                <td class="py-2 px-2 text-center border-b border-gray-200 relative">
                   <!-- Existing Pinpoint Button (hidden on hover) -->

                   <!-- NEW 'Click to Locate' Overlay (visible on hover) -->
                   <div class="absolute bottom-1 right-2 transition-opacity duration-300 ease-in-out opacity-0 group-hover:opacity-80 pointer-events-none">
                      <!-- Apply Tag Styling & Force Single Line -->
                      <div class="inline-block px-2 py-1 rounded bg-teal-300 text-teal-800 shadow-sm mb-1 whitespace-nowrap text-sm font-medium">
                         <!-- Inner flex for alignment -->
                         <div class="inline-flex items-center space-x-1">
                            <!-- Simple Crosshairs Icon - Smaller -->
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 10l4 4m0-4l-4 4" />
                            </svg>
                            <span>Click to Locate</span>
                         </div>
                      </div>
                   </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

        <!-- Mobile Card View Container - h-full and scroll -->
        <div ref="mobileScrollContainer" class="block md:hidden py-2 h-full overflow-y-auto">
           <!-- Add empty state for Mobile -->
           <div v-if="!loading && !apiError && !processedDrivers.length" class="text-center py-10 text-gray-500">
             <p>No tracking data available.</p>
           </div>
      <!-- Mobile Card Loop -->
           <div v-if="processedDrivers.length > 0" v-for="(driver, index) in processedDrivers"
                :key="driver.user_id || driver.mobile_number || index + '-mobile'"
                :data-mobile-number="driver.mobile_number"
                class="bg-white rounded-lg shadow p-4 mb-4 cursor-pointer hover:shadow-md transition-shadow duration-150 ease-in-out group relative overflow-hidden"
                :class="{ 'bg-gradient-to-tl from-teal-100 to-white': driver.mobile_number === hoveredDriverMobile }"
                @click="zoomToDriver(driver)"
           >
        <!-- Card Header: Number and Name -->
        <div class="flex justify-between items-start mb-3">
           <div class="flex items-center">
                        <!-- Add Index Number Span -->
             <span class="text-sm font-semibold text-gray-500 mr-2">#{{ index + 1 }}</span>
                        <!-- Remove tag style, use normal heading for name -->
             <h3 class="font-semibold text-lg text-gray-800 mr-2">{{ formatUserName(driver['user_name']) || 'N/A' }}</h3>
           </div>
        </div>

        <!-- Details Grid -->
        <div class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <!-- Mobile Number -->
          <div class="col-span-1">
            <span class="font-medium text-gray-500 flex items-center mb-0.5">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
               Mobile
            </span>
            <div class="flex items-center justify-between">
               <span class="text-blue-600 text-sm truncate block mr-2" :title="driver['mobile_number']">{{ driver['mobile_number'] || 'N/A' }}</span>
               <button
                  v-if="driver['mobile_number']"
                  @click="copyToClipboard(driver['mobile_number'], driver['mobile_number'] + '-mobile-btn')"
                  :title="copiedItemId === driver['mobile_number'] + '-mobile-btn' ? 'Copied!' : 'Copy Mobile'"
                  class="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
                >
                  <svg v-if="copiedItemId === driver['mobile_number'] + '-mobile-btn'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
               </button>
            </div>
          </div>

          <!-- Plate Number -->
          <div class="col-span-1">
            <span class="font-medium text-gray-500 flex items-center mb-0.5">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
               Plate
            </span>
            <span class="text-gray-800 truncate block" :title="driver['vechile_plate']">
              <!-- Apply tag styling -->
                   <span class="inline-block px-2 py-0.5 rounded bg-teal-200 text-teal-800 text-xs">
                 {{ formatPlateNumber(driver['vechile_plate']) }}
              </span>
            </span>
          </div>

           <!-- Car Model -->
          <div class="col-span-1">
            <span class="font-medium text-gray-500 flex items-center mb-0.5">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
               Car Model
            </span>
            <span class="text-gray-800 truncate block" :title="driver['vechile_model']">{{ driver['vechile_model'] || 'N/A' }}</span>
          </div>

          <!-- NEW Group Field -->
          <div class="col-span-1">
             <span class="font-medium text-gray-500 flex items-center mb-0.5">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
               Group
             </span>
            <span
               v-if="driver.group_name"
               :class="['inline-block', 'px-2', 'py-0.5', 'rounded', 'text-xs', getGroupColor(driver.group_name)]"
            >
               {{ driver.group_name?.replace(/dkilo /i, '') || 'N/A' }}
             </span>
             <span v-else class="text-gray-500 text-xs">N/A</span>
        </div>

             </div> <!-- End Details Grid -->

             <!-- Conditional Overlay -->
             <div class="absolute bottom-0 right-0 px-3 py-2 rounded-tl-lg transition-opacity duration-300 ease-in-out"
                  :class="{ 'opacity-80': driver.mobile_number === hoveredDriverMobile, 'opacity-0': driver.mobile_number !== hoveredDriverMobile }"
             >
               <div class="inline-flex items-center space-x-1 text-sm font-medium text-teal-800">
                  <!-- Simple Crosshairs Icon -->
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 10l4 4m0-4l-4 4" />
                  </svg>
                  <span>Locate</span>
     </div>
    </div>
            <!-- End Conditional Overlay -->

           </div> <!-- End v-for card -->

           <!-- Padding Div for scrolling last card into view -->
           <div class="h-[20vh] flex-shrink-0"></div>

        </div> <!-- End Mobile Card Container -->

      </div> <!-- End Table/Cards Wrapper -->

    </div> <!-- End NEW Wrapper Below Header -->

  </div> <!-- END Outermost p-6 container -->
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";
import store from "../../store";
import axios from 'axios';

// --- Define Custom Overlay Class MOVED ---

// --- Create SINGLE Loader instance outside the component definition ---
// --- Environment Specific Config (Select ONE block) ---
// --- Development ---
// const loaderOptions = {
//   apiKey: "AIzaSyBg8BVLET0Q-tHhAIgr-rmo3ddQiwgFgCo", // dev
//   version: 'weekly',
//   libraries: ['geometry'],
// };

// --- Production ---
const loaderOptions = {
  apiKey: "AIzaSyCiLDfWO0dPjiiJEUqYWoM_LjUAJQOgyGk", // prod
  version: 'weekly',
  libraries: ['visualization'],
  // Add any other production-specific loader options here
};
// --- End Environment Specific Config ---

const loader = new Loader(loaderOptions);
// --- End Loader Instance ---

export default {
  data() {
    return {
      borderRadius: '15px', // Border radius applied directly to the table and map
      countdownTime: 119, // Set the initial countdown time to 01:59
      drivers:[],
      copiedItemId: null, // Added for copy notification
      loading: false, // Added loading state
      apiError: false, // Added error state
      sortColumn: 'user_name', // Default sort column
      sortDirection: 'asc', // Default sort direction
      searchQuery: '',
      mapInstance: null,
      markers: {},
      hoveredDriverMobile: null, // Added for desktop hover tracking
      fitZoomLevel: null, // NEW: Store the zoom level after fitting bounds
      cardIntersectionObserver: null, // NEW: Observer for mobile cards
      infoWindow: null, // NEW: Google Maps InfoWindow instance
      currentTargetDriver: null, // NEW: Driver being zoomed to
      isSortMenuOpen: false, // NEW: For mobile sort dropdown
      sortOptions: [        // NEW: Mobile sort options
        { value: 'user_name', text: 'Cruiser' },
        { value: 'group_name', text: 'Group' }, // ADDED
        { value: 'mobile_number', text: 'Mobile' },
        { value: 'vechile_model', text: 'Car Model' },
        { value: 'vechile_plate', text: 'Plate' },
        // Add other potential sort columns if needed
      ],
      // --- Animation State ---
      isAnimatingZoom: false,
      animationStartTime: null,
      animationDuration: 3000, // Default for driver zoom
      panZoomDuration: 1000, // NEW: Duration for fit bounds zoom
      startZoom: null,
      targetZoom: null,
      startCenter: null,
      targetCenter: null,
      animationFrameId: null,
      // --- End Animation State ---
    };
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.countdownTime / 60);
      const seconds = this.countdownTime % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    processedDrivers() {
      if (!this.drivers) return [];

      let filtered = [...this.drivers]; // Start with a copy

      // 1. Filtering based on searchQuery
      if (this.searchQuery) {
        const lowerQuery = this.searchQuery.toLowerCase();
        filtered = filtered.filter(driver => {
           return (
             driver.user_name?.toLowerCase().includes(lowerQuery) ||
             driver.mobile_number?.toLowerCase().includes(lowerQuery) ||
             driver.vechile_model?.toLowerCase().includes(lowerQuery) ||
             driver.vechile_plate?.toLowerCase().includes(lowerQuery)
          );
        });
      }

      // 2. Sorting the filtered results
      const sortKey = this.sortColumn;
      const direction = this.sortDirection === 'asc' ? 1 : -1;

      if (sortKey) {
          filtered.sort((a, b) => {
              let valA = a[sortKey];
              let valB = b[sortKey];

              // Handle null/undefined before attempting operations
              if (valA == null && valB == null) return 0;
              if (valA == null) return -1 * direction; // Sort nulls lower
              if (valB == null) return 1 * direction;

              // Specific handling for user_name (case-insensitive)
              if (sortKey === 'user_name') {
                 valA = String(valA).toLowerCase();
                 valB = String(valB).toLowerCase();
              }
               // Add handling for other columns if needed (e.g., numeric parsing)
               // Assuming other columns are comparable as strings for now

              // Final Comparison
              if (valA < valB) return -1 * direction;
              if (valA > valB) return 1 * direction;
              return 0;
          });
      }

      return filtered; // Return filtered and sorted array
    },
    cruiserCount() {
      return this.drivers.length;
    },
    currentSortOptionText() { // NEW: For mobile sort dropdown
      const selected = this.sortOptions.find(opt => opt.value === this.sortColumn);
      return selected ? selected.text : 'Sort';
    }
  },

  methods: {
    async fetchDrivers() {
      this.loading = true;
      this.apiError = false;
            try {
                let token = localStorage.getItem('token');
                const response = await axios.get('https://publish.dkilo.com/api/screen-track',{
                    headers: { token, "Country-Code": 'SA' },
                });
                this.drivers = response.data;
        this.initGoogleMap();
        // Setup observer after data is loaded and map initialized (nextTick ensures DOM is updated)
        this.$nextTick(() => {
          this.setupMobileScrollObserver();
        });
            } catch (error) {
                console.error('Error fetching drivers:', error);
        this.apiError = true;
      } finally {
        this.loading = false;
            }
    },
    initGoogleMap() {
      // --- REMOVED Loader instantiation from here ---

      const self = this; // Capture component context

      // Use the module-level loader instance
      loader.load().then(() => {

        const BaseOverlay = google.maps.OverlayView; // Assign base class locally

        // --- Define Custom Overlay Class HERE and store on component instance ---
        self.SmoothCircleOverlayClass = class SmoothCircleOverlay extends BaseOverlay {
          constructor(latLng, map, driverData, componentInstance) { // Add componentInstance
            super();
            this.latLng_ = latLng;
            this.map_ = map;
            this.driverData_ = driverData;
            this.div_ = null;
            this.state_ = 'default';
            this.componentInstance_ = componentInstance; // Store instance
            this.setMap(map);
          }
          onAdd() {
            const div = document.createElement("div");
            div.style.borderStyle = "none";
            div.style.borderWidth = "0px";
            div.style.position = "absolute";
            div.style.display = 'block';
            div.style.cursor = "pointer";
            div.classList.add('map-circle-overlay');
            div.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
            div.style.opacity = '0.7';
            // div.style.transform = 'scale(1)'; // Remove initial inline transform

            this.div_ = div;
            const panes = this.getPanes();
            if (panes) {
                panes.overlayMouseTarget.appendChild(div);
            } else {
                console.error("Could not get map panes.");
            }
            this.div_.addEventListener("click", (event) => {
                event.stopPropagation();
                // console.log("Overlay clicked:", this.driverData_.mobile_number);
                // Call showInfoWindow on the Vue component instance
                if (this.componentInstance_ && typeof this.componentInstance_.showInfoWindow === 'function') {
                  this.componentInstance_.showInfoWindow(this.driverData_);
                } else {
                  console.error("Component instance or showInfoWindow method not available on overlay.");
                }
            });

            // --- ADD HOVER LISTENERS ---
            this.div_.addEventListener("mouseover", () => {
                if (this.componentInstance_ && typeof this.componentInstance_.handleDesktopHover === 'function') {
                   this.componentInstance_.handleDesktopHover(this.driverData_);
                }
            });
            this.div_.addEventListener("mouseout", () => {
                 if (this.componentInstance_ && typeof this.componentInstance_.handleDesktopLeave === 'function') {
                     this.componentInstance_.handleDesktopLeave();
                 }
            });
            // --- END ADD HOVER LISTENERS ---
          }
          draw() {
            const projection = this.getProjection();
            if (!projection || !this.div_) return;
            const sw = projection.fromLatLngToDivPixel(this.latLng_);

            if (sw) {
                // --- Remove size setting from draw ---
                // const size = this.getSize_();
                // Use a fixed base size for positioning offset
                const baseSize = 16; // Matches the default CSS size
                this.div_.style.left = sw.x - baseSize / 2 + "px";
                this.div_.style.top = sw.y - baseSize / 2 + "px";
                // REMOVE setting width/height here
                // this.div_.style.width = size + 'px';
                // this.div_.style.height = size + 'px';
                // --- End Remove ---
                // console.log(`[DEBUG draw applied] Mobile: ${this.driverData_?.mobile_number}, Left: ${this.div_.style.left}, Top: ${this.div_.style.top}`);
            } else {
                 // console.warn(`[DEBUG draw] Invalid coordinates for ${this.driverData_?.mobile_number}`);
            }
          }
          onRemove() {
            if (this.div_) {
              this.div_.parentNode.removeChild(this.div_);
              this.div_ = null;
            }
          }
          update(state) {
              // console.log(`[DEBUG update] Setting state: ${state} for ${this.driverData_?.mobile_number}`);
              if (!this.div_ || this.state_ === state) return;
              this.state_ = state;
              this.div_.classList.remove('highlighted', 'dimmed');
              // --- Remove size calculation ---
              // let size = 16;
              switch (state) {
                  case 'highlighted':
                      this.div_.classList.add('highlighted');
                      // size = 24;
                      break;
                  case 'dimmed':
                      this.div_.classList.add('dimmed');
                      // size = 12;
                      break;
                  case 'default':
                  default:
                      // size = 16;
                      break;
              }
              // --- End Remove ---
              // Still need to call draw to potentially reposition if needed,
              // but it won't interfere with size/scale animation now.
              this.draw();
          }
        }
        // --- End Custom Overlay Class Definition ---

        const mapElement = document.getElementById("googleMap");
        if (!mapElement) { console.error("Map element not found"); return; }

        // Use self reference for component properties/methods
        if (!self.mapInstance) {
             self.mapInstance = new google.maps.Map(mapElement, {
               center: { lat: 26.4207, lng: 50.0875 },
          zoom: 13,
          styles: [
                 { elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
            { elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
                 { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
            { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#b0b0b0' }] },
            { featureType: 'administrative.country', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative.land_parcel', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative.locality', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
                 { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#e6e6e6' }] },
            { featureType: 'poi.business', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
                 { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#c8e6c9' }] },
                 { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#388e3c' }] },
                 { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
            { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
            { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
            { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#e0e0e0' }] },
                 { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9e2f3' }] },
                 { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#0277bd' }] }
               ],
               clickableIcons: false,
               disableDefaultUI: true, // Disable all default controls
               mapDataControl: false // Add this to try and hide map data/terms links
             });
             // Initialize InfoWindow AFTER map instance exists
             self.infoWindow = new google.maps.InfoWindow();
             // Add listener to close InfoWindow when map is clicked
             self.mapInstance.addListener('click', () => {
                 if (self.infoWindow) {
                     self.infoWindow.close();
                 }
             });
        }

        self.clearOverlays(); // Use self reference

        const bounds = new google.maps.LatLngBounds();
        self.drivers.forEach(driver => { // Use self reference
          if (!driver.latitude || !driver.longitude || !driver.mobile_number) {
            console.warn("Skipping overlay for driver due to missing data:", driver.mobile_number);
            return;
          }

          const position = new google.maps.LatLng(parseFloat(driver.latitude), parseFloat(driver.longitude));
          bounds.extend(position);

          // Create overlay using the stored class definition, PASS SELF
          const overlay = new self.SmoothCircleOverlayClass(position, self.mapInstance, driver, self); // Use stored class
          self.markers[driver.mobile_number] = overlay; // Use self reference
        });

        // Fit bounds initially (moved to zoomToFitAllDrivers, but call it once here)
        self.zoomToFitAllDrivers();

      }).catch(e => {
        console.error("Error loading Google Maps API:", e);
        self.apiError = true; // Use self reference
      });
    },

    startCountdown() {
      setInterval(() => {
        if (this.countdownTime > 0) {
          this.countdownTime--;
        } else {
          this.countdownTime = 119;
        }
      }, 1000);
    },

    formatUserName(name) {
        if (!name) return '-';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 0) return '-';
        const formattedParts = parts.slice(0, 2).map(part =>
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        );
        return formattedParts.join(' ');
    },

    formatPlateNumber(plate) {
      if (!plate) return '-';
      const plateStr = String(plate);
      const match = plateStr.match(/^(\d+)\s*([a-zA-Z]+)$|^([a-zA-Z]+)\s*(\d+)$/);
      let numberPart = '-';
      let textPart = '-';
      if (match) {
        if (match[1] && match[2]) { numberPart = match[1]; textPart = match[2].toUpperCase(); }
        else if (match[3] && match[4]) { numberPart = match[4]; textPart = match[3].toUpperCase(); }
        return `${numberPart} ${textPart}`;
      } else {
          const numbers = plateStr.replace(/[^0-9]/g, '');
          const letters = plateStr.replace(/[^a-zA-Z]/g, '').toUpperCase();
          if (numbers && letters) { return `${numbers} ${letters}`; }
          else if (numbers) { return numbers; }
          else if (letters) { return letters; }
      }
      return plateStr.toUpperCase();
    },

    copyToClipboard(text, id) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        this.copiedItemId = id;
        setTimeout(() => {
          if (this.copiedItemId === id) {
            this.copiedItemId = null;
          }
        }, 1500);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    },

    sortBy(columnKey) {
      this.selectSortOption(columnKey);
    },

    zoomToDriver(driver) {
      if (!this.mapInstance || this.isAnimatingZoom) {
        // ... already animating check ...
        return;
      }
      this.infoWindow?.close();
      try {
        const targetLat = parseFloat(driver.latitude);
        const targetLng = parseFloat(driver.longitude);
        if (isNaN(targetLat) || isNaN(targetLng)) {
           console.error("[zoomToDriver] Invalid target LatLng.");
           return;
        }
        const targetLatLng = new google.maps.LatLng(targetLat, targetLng);
        const targetZoomLevel = 15; // Our target zoom

        // --- Check if already at target (still useful) ---
        const currentCenter = this.mapInstance.getCenter();
        const currentZoom = this.mapInstance.getZoom();
        if (currentCenter && currentZoom !== undefined &&
            google.maps.geometry.spherical.computeDistanceBetween(currentCenter, targetLatLng) < 1 &&
            Math.abs(currentZoom - targetZoomLevel) < 0.1)
        {
            // Optionally re-open info window if closed
            this.showInfoWindow(driver);
            return;
        }
        // --- End Check ---

        // Store the target driver for InfoWindow later
        this.currentTargetDriver = driver;

        // Cancel any previous animation frame
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
        }

        // Check if target is currently visible
        const mapBounds = this.mapInstance.getBounds();
        const isTargetVisible = mapBounds ? mapBounds.contains(targetLatLng) : false;

        // Set animation parameters based on visibility
        this.startZoom = currentZoom;
        this.startCenter = currentCenter;
        this.targetCenter = targetLatLng;
        this.targetZoom = targetZoomLevel;
        this.animationStartTime = performance.now();
        this.isAnimatingZoom = true;

        if (isTargetVisible) {
            // Target is VISIBLE: Simple Pan/Zoom
            this.animationDuration = this.panZoomDuration; // Use shorter duration
            // No intermediate zoom needed for simple pan
            this._intermediateZoom = null; // Ensure this is null if not used by simple animation step
            this.animationFrameId = requestAnimationFrame(this.animateMapStepSimple);
        } else {
            // Target is NOT VISIBLE: Full Zoom Out/In Animation
            const defaultFitZoom = 11;
            const intermediateZoomTarget = this.fitZoomLevel !== null ? this.fitZoomLevel : defaultFitZoom;
            this.animationDuration = 3000; // Use longer duration for 3-phase
            this._intermediateZoom = intermediateZoomTarget; // Set the intermediate zoom level
            this.animationFrameId = requestAnimationFrame(this.animateMapStep);
        }

        // Logs moved or removed as they are now conditional
        // console.log(`[zoomToDriver] Start Zoom: ${this.startZoom}, Intermediate Target: ${this._intermediateZoom}, Target Zoom: ${this.targetZoom}`);
        // console.log(`[zoomToDriver] Start Center: ${this.startCenter.toString()}, Target Center: ${this.targetCenter.toString()}`);

      } catch (error) {
         console.error("[zoomToDriver] Error initiating animation:", error);
         this.isAnimatingZoom = false;
         this.currentTargetDriver = null;
      }
    },

    animateMapStep(timestamp) {
        if (!this.isAnimatingZoom || !this.mapInstance || !this.startCenter || !this.targetCenter) {
            // ... cleanup code ...
            return;
        }

        const elapsed = timestamp - this.animationStartTime;
        let progress = Math.min(elapsed / this.animationDuration, 1);
        const easeInOutQuad = t => t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const easedProgress = easeInOutQuad(progress);

        // Define phase durations
        const phase1DurationRatio = 0.25; // Zoom out duration
        const pauseDurationRatio = 0.10;  // Pause duration
        const phase2StartRatio = phase1DurationRatio + pauseDurationRatio; // When phase 3 (pan/zoom in) starts

        let currentZoom;
        let currentCenter;
        const intermediateZoomTarget = this._intermediateZoom;

        if (easedProgress < phase1DurationRatio) {
            // Phase 1: Zoom Out In Place
            const phaseProgress = easedProgress / phase1DurationRatio;
            currentZoom = this.startZoom + (intermediateZoomTarget - this.startZoom) * phaseProgress;
            currentCenter = this.startCenter; // Keep center fixed

        } else if (easedProgress < phase2StartRatio) {
            // Phase 2: Pause
            currentZoom = intermediateZoomTarget; // Hold zoom
            currentCenter = this.startCenter;     // Hold center

        } else {
            // Phase 3: Pan and Zoom In
            const phaseProgress = (easedProgress - phase2StartRatio) / (1 - phase2StartRatio);
            const zoomStartPhase3 = intermediateZoomTarget;
            currentZoom = zoomStartPhase3 + (this.targetZoom - zoomStartPhase3) * phaseProgress;
            // Pan from start center to target center
            currentCenter = google.maps.geometry.spherical.interpolate(this.startCenter, this.targetCenter, phaseProgress);
        }

        this.mapInstance.moveCamera({ center: currentCenter, zoom: currentZoom });

        if (progress < 1) {
            this.animationFrameId = requestAnimationFrame(this.animateMapStep);
        } else {
            // Animation finished - ensure final state
            this.mapInstance.moveCamera({ center: this.targetCenter, zoom: this.targetZoom });

            if (this.currentTargetDriver) {
                this.showInfoWindow(this.currentTargetDriver);
            }

            // Reset animation state
            this.isAnimatingZoom = false;
            this.animationFrameId = null;
            this.startZoom = null;
            this.startCenter = null;
            this.targetZoom = null;
            this.targetCenter = null;
            this._intermediateZoom = null;
            this.currentTargetDriver = null;
        }
    },

    // NEW: Simple Pan/Zoom Animation Step
    animateMapStepSimple(timestamp) {
        if (!this.isAnimatingZoom || !this.mapInstance || !this.startCenter || !this.targetCenter) {
            this.isAnimatingZoom = false;
            this.animationFrameId = null;
            return;
        }

        const elapsed = timestamp - this.animationStartTime;
        // Use panZoomDuration for this animation type
        let progress = Math.min(elapsed / this.panZoomDuration, 1);
        const easeInOutQuad = t => t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const easedProgress = easeInOutQuad(progress);

        // Direct interpolation
        const currentCenter = google.maps.geometry.spherical.interpolate(this.startCenter, this.targetCenter, easedProgress);
        const currentZoom = this.startZoom + (this.targetZoom - this.startZoom) * easedProgress;

        this.mapInstance.moveCamera({ center: currentCenter, zoom: currentZoom });

        if (progress < 1) {
            this.animationFrameId = requestAnimationFrame(this.animateMapStepSimple);
        } else {
            // Animation finished
            this.mapInstance.moveCamera({ center: this.targetCenter, zoom: this.targetZoom });

            // Show info window for the target driver
            if (this.currentTargetDriver) {
                this.showInfoWindow(this.currentTargetDriver);
            }

            // Reset animation state
            this.isAnimatingZoom = false;
            this.animationFrameId = null;
            this.startZoom = null;
            this.startCenter = null;
            this.targetZoom = null;
            this.targetCenter = null;
            this.currentTargetDriver = null; // Clear target driver
        }
    },

    // --- Hover Handling Methods (Update uses overlay.update) ---
    handleDesktopHover(driver) {
      if (driver && driver.mobile_number) {
         this.hoveredDriverMobile = driver.mobile_number;
         this.updateMarkerAppearance();
      }
    },

    handleDesktopLeave() {
      this.hoveredDriverMobile = null;
      this.updateMarkerAppearance();
    },

    updateMarkerAppearance() {
      if (!this.markers || Object.keys(this.markers).length === 0) {
         return;
      }
       const highlightedMobile = this.hoveredDriverMobile;
       for (const mobileNumber in this.markers) {
          const overlay = this.markers[mobileNumber];
          if (!overlay || typeof overlay.update !== 'function') continue;
          let stateToSet = 'default';
          if (highlightedMobile && mobileNumber === highlightedMobile) {
            stateToSet = 'highlighted';
          } else if (highlightedMobile) {
            stateToSet = 'dimmed';
          }
          overlay.update(stateToSet);
       }
    },

    // Helper to remove existing overlays
    clearOverlays() {
      // ... clearOverlays logic ...
    },

    // Add zoomToFitAllDrivers method
    zoomToFitAllDrivers() {
       if (!this.mapInstance || !this.drivers || this.drivers.length === 0) {
          console.warn("[zoomToFitAllDrivers] Map not ready or no drivers to fit.");
          return;
       }
       if (this.isAnimatingZoom) {
           console.warn("[zoomToFitAllDrivers] Animation already in progress.");
           return;
       }

       this.infoWindow?.close(); // Close info window
       this.currentTargetDriver = null; // Ensure no target driver

       // Store starting position
       const currentCenter = this.mapInstance.getCenter();
       const currentZoom = this.mapInstance.getZoom();
       if (!currentCenter || currentZoom === undefined) {
           console.error("[zoomToFitAllDrivers] Could not get valid start center/zoom.");
           return; // Prevent issues if map isn't ready
       }

       const bounds = new google.maps.LatLngBounds();
       let validPoints = 0;
       this.drivers.forEach(driver => {
         if (driver.latitude && driver.longitude) {
           try {
               const lat = parseFloat(driver.latitude);
               const lng = parseFloat(driver.longitude);
               if (!isNaN(lat) && !isNaN(lng)) {
                    bounds.extend(new google.maps.LatLng(lat, lng));
                    validPoints++;
               }
           } catch (e) { /* Ignore parse errors */ }
         }
       });

       if (validPoints > 0 && !bounds.isEmpty()) {
         this.mapInstance.fitBounds(bounds);
         // Start animation AFTER map is idle at the new bounds
         google.maps.event.addListenerOnce(this.mapInstance, 'idle', () => {
            // Get final state after fitBounds
            const finalCenter = this.mapInstance.getCenter();
            const currentIdleZoom = this.mapInstance.getZoom();
            const maxZoom = 16;
            let finalZoom = currentIdleZoom;
            if (currentIdleZoom > maxZoom) {
                 this.mapInstance.setZoom(maxZoom);
                 finalZoom = maxZoom;
             }
            this.fitZoomLevel = finalZoom; // Store the fit zoom level

            // Check if start/end are different enough to warrant animation
            if (google.maps.geometry.spherical.computeDistanceBetween(currentCenter, finalCenter) > 1 || Math.abs(currentZoom - finalZoom) > 0.1) {
               console.log("[zoomToFitAllDrivers] Initiating smooth pan/zoom animation...");
               // Setup animation state
               this.startCenter = currentCenter;
               this.startZoom = currentZoom;
               this.targetCenter = finalCenter;
               this.targetZoom = finalZoom;
               this.animationStartTime = performance.now();
               this.animationDuration = this.panZoomDuration; // Use specific duration
               this.isAnimatingZoom = true;

               // Cancel previous frame just in case
               if (this.animationFrameId) {
                 cancelAnimationFrame(this.animationFrameId);
               }
               // Start the simple animation
               this.animationFrameId = requestAnimationFrame(this.animateMapStepSimple);
            } else {
                console.log("[zoomToFitAllDrivers] Map already at target view. Skipping animation.");
            }
          });
       } else {
            console.warn("[zoomToFitAllDrivers] No valid driver locations to fit bounds.");
       }
    },

    // --- Mobile Scroll Interaction ---
    setupMobileScrollObserver() {
      const rootElement = this.$refs.mobileScrollContainer;
      if (!rootElement) {
        console.warn("[Observer] Mobile scroll container not found.");
        return;
      }

      // Disconnect previous observer if exists
      if (this.cardIntersectionObserver) {
        this.cardIntersectionObserver.disconnect();
      }

      const options = {
        root: rootElement,
        rootMargin: '5px 0px -90% 0px', // Zone = Top 10%, Offset 0px
        threshold: 0 // Trigger immediately
      };

      this.cardIntersectionObserver = new IntersectionObserver(this.handleCardIntersection, options);

      const cards = rootElement.querySelectorAll('[data-mobile-number]');
      cards.forEach(card => {
        this.cardIntersectionObserver.observe(card);
      });
      console.log(`[Observer] Setup complete, observing ${cards.length} cards.`);

      // Manually trigger check after a short delay to catch initial state
      setTimeout(() => {
        if (this.cardIntersectionObserver) {
            this.handleCardIntersection(this.cardIntersectionObserver.takeRecords());
        }
      }, 500); // 150ms delay, adjust if needed
    },

    handleCardIntersection(entries) {
      let highestIntersectingCard = null;
      let minRelativeTop = Infinity;
      const rootElement = this.$refs.mobileScrollContainer;
      // Get container's top position only if it exists
      const containerTop = rootElement ? rootElement.getBoundingClientRect().top : 0;

      entries.forEach(entry => {
          // Only consider entries that are currently intersecting the target zone
          if (entry.isIntersecting) {
              const absoluteTop = entry.boundingClientRect.top;
              const relativeTop = absoluteTop - containerTop; // Calculate position relative to container
              if (relativeTop < minRelativeTop) {
                  minRelativeTop = relativeTop;
                  highestIntersectingCard = entry.target;
              }
          }
      });

      // Determine the mobile number of the highest card found in this specific callback run
      const newMobileNumber = highestIntersectingCard ? highestIntersectingCard.dataset.mobileNumber : null;

      // Update only if a new highest card is found and it's different from the current one
      if (newMobileNumber && this.hoveredDriverMobile !== newMobileNumber) {
          this.hoveredDriverMobile = newMobileNumber;
          this.updateMarkerAppearance();
      }
    },
    // --- End Mobile Scroll Interaction ---

    // NEW: Show InfoWindow for a driver
    showInfoWindow(driver) {
      if (!this.infoWindow || !this.mapInstance || !driver || !driver.latitude || !driver.longitude) {
        console.warn("[showInfoWindow] Missing infoWindow, map instance, or valid driver data.", driver);
        return;
      }

      this.infoWindow.close(); // Close any previous window

      try {
        const latLng = new google.maps.LatLng(parseFloat(driver.latitude), parseFloat(driver.longitude));

        // Update styles
        const contentStyle = 'font-family: sans-serif; padding: 5px;';
        const titleStyle = 'font-weight: bold; font-size: 1.1em; margin-bottom: 8px;'; // Increased margin
        const lineStyle = 'display: flex; align-items: center; margin-bottom: 6px; font-size: 0.9em;'; // Increased margin
        const iconStyle = 'width: 16px; height: 16px; margin-right: 6px; opacity: 0.7;';
        // Darker tag style
        const tagStyle = 'display: inline-block; background-color: #319795; color: #E6FFFA; padding: 2px 6px; border-radius: 99px; font-size: 0.8em; margin-left: 5px;';

        // Construct HTML content (using updated styles)
        let contentString = `<div style="${contentStyle}">`;
        contentString += `<div style="${titleStyle}">${this.formatUserName(driver.user_name || 'N/A')}</div>`;

        // Mobile Number
        contentString += `<div style="${lineStyle}">`;
        contentString += `<svg style="${iconStyle}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>`;
        contentString += `<span>${driver.mobile_number || 'N/A'}</span>`;
        contentString += `</div>`;

        // Car Model
        contentString += `<div style="${lineStyle}">`;
        contentString += `<svg style="${iconStyle}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>`;
        contentString += `<span>${driver.vechile_model || 'N/A'}</span>`;
        contentString += `</div>`;

        // Plate Number
        contentString += `<div style="${lineStyle}">`;
        contentString += `<svg style="${iconStyle}" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>`;
        contentString += `<span>Plate:</span><span style="${tagStyle}">${this.formatPlateNumber(driver.vechile_plate || 'N/A')}</span>`;
        contentString += `</div>`;

        contentString += `</div>`;

        this.infoWindow.setContent(contentString);
        this.infoWindow.setPosition(latLng);
        this.infoWindow.open(this.mapInstance);

      } catch (error) {
        console.error("[showInfoWindow] Error creating LatLng or showing window:", error);
      }
    },

    // --- Mobile Sort Dropdown Methods ---
    toggleSortDirection() {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    selectSortOption(value) {
      if (this.sortColumn === value) {
        // Clicked the same option, toggle direction
        this.toggleSortDirection();
      } else {
        // Clicked a new option, set column and default direction
        this.sortColumn = value;
        // Let's default to 'asc' when changing column, feel free to change
        this.sortDirection = 'asc';
      }
      this.isSortMenuOpen = false; // Close menu
    },
    // --- End Mobile Sort Methods ---

    // NEW: Method copied from Screens.vue
    getGroupColor(groupName) {
      if (!groupName) return 'bg-gray-100 text-gray-700'; // Default
      const colors = [
        'bg-blue-100 text-blue-800',
        'bg-purple-100 text-purple-800',
        'bg-pink-100 text-pink-800',
        'bg-orange-100 text-orange-800',
        'bg-indigo-100 text-indigo-800',
        'bg-cyan-100 text-cyan-800',
        'bg-lime-100 text-lime-800',
        'bg-red-100 text-red-800',      // Added Red
        'bg-yellow-100 text-yellow-800', // Added Yellow
        'bg-green-100 text-green-800',  // Added Green
        'bg-teal-100 text-teal-800',    // Added Teal
      ];
      // Simple hash function to pick a color based on name
      let hash = 0;
      for (let i = 0; i < groupName.length; i++) {
        hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },

  },
  beforeUnmount() {
      this.clearOverlays();
      // Cancel animation frame if component is destroyed
      if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
          this.animationFrameId = null;
          this.isAnimatingZoom = false; // Reset flag
      }
      // Disconnect observer
      if (this.cardIntersectionObserver) {
        this.cardIntersectionObserver.disconnect();
        this.cardIntersectionObserver = null;
      }
      // Close info window on unmount
      if (this.infoWindow) {
        this.infoWindow.close();
        this.infoWindow = null;
      }
  },
  mounted() {
     this.fetchDrivers(); // 'this' is correct here
    this.startCountdown();
    document.title = "dKilo - Tracking";
  }
};
</script>

<style>
.map-container {
  display: flex;
  justify-content: center;
  width: 100%;
  height: auto;
}

#googleMap {
  height: 400px;
  width: 100%;
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

button {
  transition: background-color 0.2s ease;
}

@media (max-width: 768px) {
  .map-container {
    height: 300px;
  }
}

.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-leave-to {
  opacity: 0;
}

@keyframes pulse-dot {
  0% {
    transform: scale(1.5); /* Start at target highlighted scale */
    box-shadow: 0 0 0 0px rgba(20, 184, 166, 0.7);
  }
  50% {
    transform: scale(1.7); /* Pulse larger */
    box-shadow: 0 0 0 6px rgba(255, 165, 0, 0);
  }
  100% {
    transform: scale(1.5); /* Return to target highlighted scale */
    box-shadow: 0 0 0 0px rgba(255, 165, 0, 0);
  }
}

/* Base style for the custom map circle overlay */
.map-circle-overlay {
  /* Change background color to teal */
  background-color: #14B8A6; /* Teal-500 */
  border-radius: 50%;
  opacity: 0.7;
  transform: scale(1);
  transform-origin: center center;
  /* Ensure transition includes box-shadow */
  transition: transform 0.3s ease-out, opacity 0.3s ease-out, box-shadow 0.3s ease-out;
  width: 16px;
  height: 16px;
}

/* Highlighted state - Apply pulse animation */
.map-circle-overlay.highlighted {
  filter: opacity(1);  /* transform: scale(1.5); // Base scale set by animation */
  z-index: 1000;
   /* Remove static border/shadow if using animated shadow */
   /* border: 1px solid rgba(0,0,0,0.5); */
   /* box-shadow: 0 0 5px rgba(0,0,0,0.3); */
   animation: pulse-dot 1.5s infinite ease-in-out;
}

/* Dimmed state - Remove specific background color */
.map-circle-overlay.dimmed {
  filter: blur(0.5px) opacity(0.7);
  transform: scale(0.85);
   border: none;
   /* background-color: darkorange; // REMOVE THIS LINE */
   box-shadow: none;
}

/* Fade transition for sort dropdown menu */
.sort-menu-fade-enter-active,
.sort-menu-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sort-menu-fade-enter-from,
.sort-menu-fade-leave-to {
  opacity: 0;
}
</style>
